"use client";

import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

const sameIndexes = (left, right) =>
  left.size === right.size && [...left].every((value) => right.has(value));

const WrappedSeparatedText = ({ items = [], separator = "|" }) => {
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const [rowEndIndexes, setRowEndIndexes] = useState(
    () => new Set(items.length ? [items.length - 1] : [])
  );

  const measureRows = useCallback(() => {
    const nodes = itemRefs.current.slice(0, items.length).filter(Boolean);
    if (!nodes.length) {
      setRowEndIndexes(new Set());
      return;
    }

    const rowEnds = new Map();
    nodes.forEach((node, index) => {
      rowEnds.set(Math.round(node.offsetTop), index);
    });
    const next = new Set(rowEnds.values());
    setRowEndIndexes((current) => (sameIndexes(current, next) ? current : next));
  }, [items.length]);

  useLayoutEffect(() => {
    measureRows();
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(measureRows);
    observer.observe(container);
    return () => observer.disconnect();
  }, [measureRows, items]);

  return (
    <span ref={containerRef} className="inline [overflow-wrap:normal]">
      {items.map((item, index) => (
        <span
          key={`${item}-${index}`}
          ref={(node) => {
            itemRefs.current[index] = node;
          }}
          className="inline-flex whitespace-nowrap"
        >
          <span>{item}</span>
          {index < items.length - 1 ? (
            <span
              aria-hidden="true"
              className={`mx-[7px] ${rowEndIndexes.has(index) ? "invisible" : "visible"}`}
            >
              {separator}
            </span>
          ) : null}
        </span>
      ))}
    </span>
  );
};

export default WrappedSeparatedText;
